<template>
	<field-wrapper v-bind="wrapper" error="" @hook:mounted="init">
		<div class="square">
			<div ref="canvas">
				<Action ref="undo" class="undo" :class="{ drawing }" @click="undo">
					<IconUndo />
				</Action>
			</div>
		</div>
	</field-wrapper>
</template>

<script>
import Sketch from 'sketch-js'
import IconUndo from 'mdi-vue/Undo'
import FieldWrapper from '@/components/shared/FieldWrapper'
import Action from '@/components/shared/Action'
import field from '@/mixins/field'
import { pointDist } from '@/lib/trig'

const CANVAS_SIZE = 640
const DIST_THRESHOLD = 2

export default {
	name: 'paint',
	data() {
		return {
			ctx: null,
			box: {},
			history: [],
			drawing: false
		}
	},
	computed: {
		lastPoint() {
			const lastLine = this.history[this.history.length - 1]
			return lastLine ? lastLine[lastLine.length - 1] : null
		}
	},
	methods: {
		push(coord) {
			const i = this.history.length - 1
			this.history[i].push(coord)
		},
		init() {
			// Calculate canvas bounds
			this.resize()

			// Create Sketch context
			this.ctx = Sketch.create({
				container: this.$refs.canvas,
				fullscreen: false,
				width: CANVAS_SIZE,
				height: CANVAS_SIZE,
				autoclear: false,
				mousedown: this.drawStart,
				mousemove: this.drawMove,
				mouseup: this.drawEnd
			})
			this.ctx.strokeStyle = '#111'
			this.ctx.lineWidth = 5
			this.ctx.lineJoin = 'round'
			this.ctx.lineCap = 'round'

			// Add resize listener
			window.addEventListener('resize', this.resize)
		},
		resize() {
			this.box = this.$refs.canvas.getBoundingClientRect()
		},
		offset(...args) {
			const offset = this.box.width / CANVAS_SIZE
			return args.map(a => a / offset)
		},
		drawStart() {
			this.blurFocused()
			this.drawing = true
			const { x, y } = this.ctx.touches[0]
			this.history.push([{ x, y }])
		},
		drawMove() {
			if (this.ctx.dragging && this.ctx.touches.length) {
				// Get touch xy
				const { x, y } = this.ctx.touches[0]

				// Only add new point if far enough away from last point
				if (this.lastPoint) {
					const dist = pointDist([this.lastPoint.x, this.lastPoint.y], [x, y])
					if (dist < DIST_THRESHOLD) return
				}

				// Add point and update canvas
				this.push({ x, y })
				this.updateCanvas()
			}
		},
		drawEnd() {
			this.drawing = false
			this.updateCanvas()
		},
		undo() {
			this.history.pop()
			this.updateCanvas()
		},
		updateCanvas() {
			// Clear canvas
			this.ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)

			// Draw all existing lines to canvas
			for (const line of this.history) {
				const [first, ...rest] = line

				// Move to first point
				this.ctx.beginPath()
				this.ctx.moveTo(...this.offset(first.x, first.y))

				if (rest.length) {
					// Draw lines to all other points
					for (const { x, y } of rest) this.ctx.lineTo(...this.offset(x, y))
				} else {
					// Force canvas to draw a "dot"
					this.ctx.lineTo(...this.offset(first.x, first.y))
				}

				// Fill in stroke
				this.ctx.stroke()
			}
			this.export()
		},
		export() {
			if (this.ctx) {
				// Set model to empty string if no lines exist in history
				this.model = this.history.length ? this.ctx.canvas.toDataURL() : ''
			}
		},
		blurFocused() {
			// Un-focus any currently focused control
			const el = document.querySelector(':focus')
			if (el) el.blur()
		}
	},
	watch: {
		model(value) {
			// Clear canvas if model is cleared
			if (this.ctx && value === '') this.ctx.clear()
		}
	},
	beforeDestroy() {
		if (this.ctx) this.ctx.destroy()
		window.removeEventListener('resize', this.resize)
	},
	mixins: [field],
	components: {
		FieldWrapper,
		Action,
		IconUndo
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/components';

.square {
	@extend %square;
	position: relative;
	background-color: $blue-98;

	> div {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;

		input {
			position: absolute;
			top: 0;
			width: 0;
			height: 0;
		}

		>>> canvas {
			position: relative;
			display: block;
			width: 100%;
			height: 100%;
		}

		.undo {
			position: absolute;
			color: $blue-73;
			top: 0.25rem;
			right: 0.25rem;
			z-index: 1;

			&.drawing {
				pointer-events: none;
			}

			&:hover,
			&:focus {
				background-color: $white;
			}
			&:active {
				color: $white;
				background-color: $blue-73;
			}
		}
	}
}
</style>
